import type { Layer, Selection } from '@geosoft/viewer-sdk';
import type { ThreeDCameraMode } from '@/state/useViewerStore';

export interface ThreeDObjectNode {
  id: string;
  layerId: string;
  label: string;
  discipline: 'mechanical' | 'electrical' | 'structural' | 'general';
  visible: boolean;
  hasMissingGeometry?: boolean;
}

export interface ThreeDPerformanceSnapshot {
  fps: number;
  lod: 'high' | 'balanced' | 'reduced';
  pointBudget: number;
  droppedFrames: number;
}

export interface ThreeDHostSummary {
  label: string;
  description: string;
  narration: string;
}

export function isThreeDLayer(layer: Layer | null | undefined): boolean {
  if (!layer) return false;
  return layer.kind === '3dtiles' || layer.kind === 'bim' || layer.kind === 'terrain';
}

export function isThreeDSelection(
  selection: Selection | null,
  selectedLayer: Layer | null,
): boolean {
  if (!selection) return false;
  return isThreeDLayer(selectedLayer);
}

export function inferDiscipline(layer: Layer): ThreeDObjectNode['discipline'] {
  const marker = `${layer.label ?? ''} ${layer.id}`.toLowerCase();
  if (marker.includes('elec') || marker.includes('power')) return 'electrical';
  if (marker.includes('struct') || marker.includes('steel')) return 'structural';
  if (marker.includes('mech') || marker.includes('pipe')) return 'mechanical';
  return 'general';
}

export function buildThreeDObjectNodes(
  layers: Layer[],
  visibleLayerIds: Set<string>,
): ThreeDObjectNode[] {
  const nodes: ThreeDObjectNode[] = [];
  for (const layer of layers) {
    if (!isThreeDLayer(layer)) continue;
    nodes.push({
      id: `obj-${layer.id}`,
      layerId: layer.id,
      label: layer.label ?? layer.id,
      discipline: inferDiscipline(layer),
      visible: visibleLayerIds.has(layer.id),
      hasMissingGeometry: `${layer.id} ${layer.label ?? ''}`.toLowerCase().includes('missing'),
    });
  }
  return nodes;
}

export function cameraModeLabel(mode: ThreeDCameraMode): string {
  switch (mode) {
    case 'orbit':
      return 'Orbit';
    case 'pan':
      return 'Pan';
    case 'zoom':
      return 'Zoom';
    case 'walk':
      return 'Walk';
    case 'fly':
      return 'Fly';
    case 'look-at-selected':
      return 'Look at selected';
    default:
      return 'Orbit';
  }
}

export function buildThreeDStateNarration(args: {
  selection: Selection | null;
  cameraMode: ThreeDCameraMode;
  objectCount: number;
  performance: ThreeDPerformanceSnapshot;
}): string {
  const selected = args.selection?.featureId != null
    ? `Selected object ${String(args.selection.featureId)}.`
    : 'No object selected.';
  return `${selected} Camera mode ${cameraModeLabel(args.cameraMode)}. ${args.objectCount} objects in tree. ${args.performance.fps.toFixed(
    0,
  )} fps, point budget ${Math.round(args.performance.pointBudget)}.`;
}

export function buildThreeDHostSummary(args: {
  selection: Selection | null;
  cameraMode: ThreeDCameraMode;
  objectCount: number;
  performance: ThreeDPerformanceSnapshot;
  canvasTone: 'light' | 'neutral-dark';
}): ThreeDHostSummary {
  const label = `3D scene mode; camera ${cameraModeLabel(args.cameraMode)}; ${args.objectCount} object${
    args.objectCount === 1 ? '' : 's'
  }`;
  const description = `3D scene in ${
    args.canvasTone === 'neutral-dark' ? 'neutral dark' : 'light'
  } canvas mode. ${args.objectCount} objects listed. ${args.performance.fps.toFixed(
    0,
  )} frames per second.`;
  return {
    label,
    description,
    narration: buildThreeDStateNarration(args),
  };
}
